import { useState, useEffect, useCallback, useRef } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { getColumnKey, resolveActualKey } from "@/components/tableau/utils/formatters";

const MAX_HISTORY = 20;

export function useUndoRedo({ orders, setOrders, markRowAsModified }) {
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  // Restore a single cell value (backend + local state)
  const restoreValue = useCallback(async (orderId, columnName, value) => {
    const order = ordersRef.current.find(o => o.order_id === orderId);
    if (!order) return;
    const key = resolveActualKey(order.extracted_data || {}, columnName);
    const updatedData = { ...order.extracted_data, [key]: value };
    try {
      await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: updatedData }),
      });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: updatedData } : o));
      markRowAsModified(orderId);
    } catch (e) {
      console.error("Undo/redo restore failed:", e);
      toast.error("Erreur lors de la restauration");
    }
  }, [setOrders, markRowAsModified]);

  // Push a new action (or batch of actions) to the undo stack
  const pushAction = useCallback((action) => {
    const actions = Array.isArray(action) ? action : [action];
    setUndoStack(prev => [...prev.slice(-(MAX_HISTORY - 1)), { actions, timestamp: Date.now() }]);
    setRedoStack([]);
  }, []);

  // Undo last action
  const undo = useCallback(async () => {
    if (!undoStack.length) return;
    const entry = undoStack[undoStack.length - 1];
    for (const action of entry.actions) {
      await restoreValue(action.orderId, action.columnName, action.oldValue);
    }
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, entry]);
    const count = entry.actions.length;
    toast.success(`Annulé (${count} cellule${count > 1 ? "s" : ""})`);
  }, [undoStack, restoreValue]);

  // Redo last undone action
  const redo = useCallback(async () => {
    if (!redoStack.length) return;
    const entry = redoStack[redoStack.length - 1];
    for (const action of entry.actions) {
      await restoreValue(action.orderId, action.columnName, action.newValue);
    }
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, entry]);
    const count = entry.actions.length;
    toast.success(`Rétabli (${count} cellule${count > 1 ? "s" : ""})`);
  }, [redoStack, restoreValue]);

  // Keyboard shortcuts: Ctrl+Z, Ctrl+Shift+Z / Ctrl+Y
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "Z" && e.shiftKey) || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [undo, redo]);

  return {
    pushAction, undo, redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoCount: undoStack.length,
    redoCount: redoStack.length,
  };
}
